import { z } from 'zod';

type LangLabels = Record<string, string>;

/**
 * Configures zod v4's global custom error messages using the provided i18n labels.
 * Call this once when the language context is available (e.g., in form components).
 * Zod v4 uses z.config({ customError }) instead of per-parse errorMap.
 */
export function configureZodErrors(labels: LangLabels): void {
  z.config({
    customError: (issue) => {
      const origin = (issue as any).origin;

      switch (issue.code) {
        case 'invalid_type':
          return labels.zodFieldRequired || 'This field is required';

        case 'too_small': {
          const minimum = (issue as any).minimum;
          if (origin === 'string') {
            if (minimum === 1) {
              return labels.zodFieldRequired || 'This field is required';
            }
            return (labels.zodTooShort || 'Minimum {min} characters').replace('{min}', String(minimum));
          }
          if (origin === 'number') {
            return (labels.zodNumberTooSmall || 'Must be at least {min}').replace('{min}', String(minimum));
          }
          if (origin === 'array') {
            return (labels.zodArrayTooShort || 'At least {min} item(s) required').replace('{min}', String(minimum));
          }
          return undefined;
        }

        case 'too_big': {
          const maximum = (issue as any).maximum;
          if (origin === 'string') {
            return (labels.zodTooLong || 'Maximum {max} characters').replace('{max}', String(maximum));
          }
          if (origin === 'number') {
            return (labels.zodNumberTooBig || 'Must be at most {max}').replace('{max}', String(maximum));
          }
          return undefined;
        }

        case 'invalid_format':
          if ((issue as any).format === 'email') {
            return labels.zodInvalidEmail || 'Invalid email address';
          }
          return labels.zodInvalidFormat || 'Invalid format';

        case 'invalid_value':
          return labels.zodInvalidSelection || 'Please select a valid option';

        case 'custom':
          // Custom issues keep their own message (set by superRefine/refine)
          return undefined;

        default:
          return undefined;
      }
    },
  });
}
